// «Активности»: шаги с целью Garmin и расход калорий с нормой InBody (обе — ступенькой: каждый день со своей
// целью), список активностей Garmin.

import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart'
import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, Select, Spinner } from '@/components/ui'
import { formatDate, formatDuration, formatNumber, formatTime } from '@/lib/format'
import { todayIso, type Period } from '@/lib/periods'
import { useSeries } from '@/pages/metrics/api'
import { dayPoints, seriesQuery } from '@/pages/metrics/series'
import { useInBodySeries } from '@/pages/inbody/api'
import { kcalNormSteps } from '@/pages/inbody/targets/logic'
import { useGarminStatus } from '@/pages/settings/garmin/api'

import { activitiesApi, activityKeys, itemsOf, type Activity } from './api'
import { SPORT_LABELS, activityTitle, expenseSummary, formatDistance, sportLabel } from './logic'

const SPORT_OPTIONS = [
  { value: '', label: 'Все виды' },
  ...Object.entries(SPORT_LABELS).map(([value, label]) => ({ value, label })),
]

function StepsCard() {
  const [period, setPeriod] = useState<Period>('month')
  const today = todayIso()
  const query = seriesQuery(period, today)
  const steps = useSeries('steps', query)
  // Цель Garmin пересчитывает почти каждый день — ступенькой по дням; дней без сохранённой цели
  // (данные до её записи по дням) — последняя известная цель ровной линией.
  const goals = useSeries('steps_goal', query)
  const status = useGarminStatus()
  const current = status.data?.steps_goal ?? null
  const goalSteps = dayPoints(goals.data?.points ?? [])
  const goal = goalSteps.length
    ? { steps: goalSteps, label: 'цель Garmin' }
    : current
      ? { value: current, label: 'цель' }
      : null
  return (
    <Card
      title="Шаги"
      subtitle={current ? `Цель Garmin сейчас — ${formatNumber(current, { digits: 0 })} шагов в день` : undefined}
    >
      <ErrorText error={steps.error} className="mb-3" />
      {steps.isPending || goals.isPending ? (
        <div className="flex h-[240px] items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <TimeSeriesChart
          series={[
            { key: 'steps', label: 'Шаги', unit: 'шагов', digits: 0, points: dayPoints(steps.data?.points ?? []) },
          ]}
          goal={goal}
          period={period}
          onPeriodChange={setPeriod}
          periods={['week', 'month', '3m', 'year']}
          height={240}
          today={today}
          emptyText="Шагов за период нет"
        />
      )}
    </Card>
  )
}

function ExpenseCard() {
  const [period, setPeriod] = useState<Period>('month')
  const today = todayIso()
  const burned = useSeries('calories_total', seriesQuery(period, today))
  // Норма InBody — суточные калории полных отчётов: с даты замера до следующего; без замеров линии нет.
  const scans = useInBodySeries('daily_kcal', {})
  const norms = kcalNormSteps(scans.data)
  const points = burned.data?.points ?? []
  return (
    <Card
      className="mt-4"
      title="Расход калорий"
      subtitle={expenseSummary(points, today, norms) ?? 'Всего за день по Garmin: обмен веществ и активность'}
    >
      <ErrorText error={burned.error} className="mb-3" />
      {burned.isPending ? (
        <div className="flex h-[240px] items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <TimeSeriesChart
          series={[{ key: 'burned', label: 'Расход', unit: 'ккал', digits: 0, points: dayPoints(points) }]}
          goal={norms.length ? { steps: norms, label: 'норма InBody' } : null}
          period={period}
          onPeriodChange={setPeriod}
          periods={['week', 'month', '3m', 'year']}
          height={240}
          today={today}
          emptyText="Расхода за период нет"
        />
      )}
    </Card>
  )
}

function ActivityRow({ a }: { a: Activity }) {
  const distance = formatDistance(a.distance_m)
  return (
    <li>
      <Link
        to={`/activities/${a.id}`}
        className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg px-2 py-2.5 hover:bg-surface-2"
      >
        {/* На телефоне название и дата — во всю ширину, метрики — строкой ниже; с sm — одна строка. */}
        <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
          <div className="truncate font-medium text-fg">{activityTitle(a)}</div>
          <div className="text-xs text-muted">
            {formatDate(a.start_at)} · {formatTime(a.start_at)} · {sportLabel(a.sport)}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 whitespace-nowrap text-sm tabular-nums text-muted">
          <span>{formatDuration(a.duration_s)}</span>
          {distance ? <span>{distance}</span> : null}
          {a.avg_hr ? <span>{a.avg_hr} уд/мин</span> : null}
          {a.calories ? <span>{formatNumber(a.calories, { digits: 0 })} ккал</span> : null}
          {a.workout_session_id ? <Badge tone="accent">тренировка</Badge> : null}
        </div>
      </Link>
    </li>
  )
}

export function ActivitiesPage() {
  const [sport, setSport] = useState('')
  const list = useInfiniteQuery({
    queryKey: activityKeys.list({ sport }),
    queryFn: ({ pageParam }) =>
      activitiesApi.list({ sport: sport || null, limit: 30, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? null,
  })
  const items = (list.data?.pages ?? []).flatMap((p) => itemsOf(p))

  return (
    <>
      <PageHeader title="Активности" description="Шаги, расход калорий и активности из Garmin: пульс, калории, нагрузка, зоны." />
      <StepsCard />
      <ExpenseCard />
      <Card
        className="mt-4"
        title="Активности"
        actions={
          <Select
            aria-label="Вид спорта"
            value={sport}
            options={SPORT_OPTIONS}
            onChange={(e) => setSport(e.target.value)}
          />
        }
      >
        <ErrorText error={list.error} />
        {list.isPending ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : items.length === 0 ? (
          <EmptyState icon="activity" title="Активностей нет" description="Они появятся после синхронизации с Garmin." />
        ) : (
          <>
            <ul className="-mx-2 divide-y divide-border">
              {items.map((a) => (
                <ActivityRow key={a.id} a={a} />
              ))}
            </ul>
            {list.hasNextPage ? (
              <div className="mt-3 flex justify-center">
                <Button loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                  Показать ещё
                </Button>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </>
  )
}
